import { useState, useRef } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sparkles, Upload, X, Camera, FileText, AlertCircle, CheckCircle2, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable modal to import an OLD offerte/factuur via AI.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - documentType: 'quote' | 'invoice'
 *  - onSaved: (savedDoc) => void  (parent refreshes list after success)
 */
export default function ImportDocumentModal({ open, onClose, documentType, onSaved }) {
  const [phase, setPhase] = useState('select'); // 'select' | 'parsing' | 'review'
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [matchedClient, setMatchedClient] = useState(null);
  const [duplicateNumber, setDuplicateNumber] = useState(false);
  const [createNewClient, setCreateNewClient] = useState(true);
  const [keepExternalNumber, setKeepExternalNumber] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const label = documentType === 'quote' ? 'Offerte' : 'Factuur';

  const resetAll = () => {
    setPhase('select');
    setFile(null);
    setFilePreview(null);
    setParsed(null);
    setMatchedClient(null);
    setDuplicateNumber(false);
    setCreateNewClient(true);
    setKeepExternalNumber(true);
    setSaving(false);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error('Bestand te groot. Max 15 MB.');
      return;
    }
    setFile(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setFilePreview(null);
    }
  };

  const handleParse = async () => {
    if (!file) return;
    setPhase('parsing');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', documentType);
      const res = await axiosInstance.post('/import/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setParsed(res.data.extracted);
      setMatchedClient(res.data.matched_client);
      setDuplicateNumber(res.data.duplicate_external_number);
      if (res.data.matched_client) setCreateNewClient(false);
      setPhase('review');
      toast.success(`AI extractie voltooid in ${res.data.elapsed_seconds}s`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'AI extractie mislukt');
      setPhase('select');
    }
  };

  const updateField = (key, value) => {
    setParsed((p) => ({ ...p, [key]: value }));
  };

  const updateItem = (idx, key, value) => {
    setParsed((p) => {
      const items = [...(p.items || [])];
      items[idx] = { ...items[idx], [key]: value };
      return { ...p, items };
    });
  };

  const addItem = () => {
    setParsed((p) => ({ ...p, items: [...(p.items || []), { description: '', quantity: 1, unit_price: 0, discount: 0, btw: 21 }] }));
  };

  const removeItem = (idx) => {
    setParsed((p) => ({ ...p, items: (p.items || []).filter((_, i) => i !== idx) }));
  };

  // Recompute live preview totals
  const computeTotals = () => {
    if (!parsed) return { subtotal: 0, btw_total: 0, total: 0 };
    const isVerlegd = parsed.btw_status === 'verlegd';
    let subtotal = 0;
    const btwAcc = {};
    (parsed.items || []).forEach((it) => {
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.unit_price) || 0;
      const disc = parseFloat(it.discount) || 0;
      const btw = isVerlegd ? 0 : (parseFloat(it.btw) || 0);
      const lineExcl = qty * price * (1 - disc / 100);
      subtotal += lineExcl;
      if (btw > 0) btwAcc[btw] = (btwAcc[btw] || 0) + (lineExcl * btw / 100);
    });
    const generalDiscount = parseFloat(parsed.general_discount) || 0;
    const btwTotal = isVerlegd ? 0 : Object.values(btwAcc).reduce((a, b) => a + b, 0);
    const total = Math.max(subtotal - generalDiscount, 0) + btwTotal;
    return { subtotal, btw_total: btwTotal, total, btw_breakdown: btwAcc };
  };

  const handleSave = async () => {
    if (!parsed?.client_name?.trim()) {
      toast.error('Klantnaam is verplicht');
      return;
    }
    if (!(parsed.items || []).length) {
      toast.error('Minimaal 1 regel verplicht');
      return;
    }
    setSaving(true);
    try {
      // Save quote/invoice (original file attachment can be added later via edit if needed)
      const payload = {
        client_id: matchedClient?.id || null,
        create_new_client: !matchedClient && createNewClient,
        external_number: parsed.external_number,
        use_external_number_if_unique: keepExternalNumber,
        client_name: parsed.client_name,
        client_email: parsed.client_email,
        client_phone: parsed.client_phone,
        address: parsed.address,
        date: parsed.date,
        expiry_date: parsed.expiry_date,
        due_date: parsed.due_date,
        items: parsed.items,
        btw_status: parsed.btw_status || 'normal',
        general_discount: parseFloat(parsed.general_discount) || 0,
        notes: parsed.notes || '',
        original_file_url: null,
      };
      const endpoint = documentType === 'quote' ? '/import/save-as-quote' : '/import/save-as-invoice';
      const res = await axiosInstance.post(endpoint, payload);
      toast.success(`${label} ${res.data.number} opgeslagen!`);
      if (onSaved) onSaved(res.data);
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const totals = computeTotals();
  const fmtEuro = (n) => `€ ${(parseFloat(n) || 0).toFixed(2).replace('.', ',')}`;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="import-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#3da5a5]" />
            <h2 className="text-xl font-bold text-gray-800">{label} importeren via AI</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClose} data-testid="import-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {phase === 'select' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                <p className="font-semibold mb-1">📷 Hoe werkt dit?</p>
                <ol className="list-decimal pl-5 space-y-1 text-xs">
                  <li>Upload een PDF of foto van een oude {label.toLowerCase()}</li>
                  <li>AI leest alle gegevens (klant, items, bedragen, BTW)</li>
                  <li>Bestaande klanten worden automatisch herkend uit het CRM</li>
                  <li>Controleer en bewerk alles voordat je opslaat</li>
                </ol>
              </div>

              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 hover:border-[#3da5a5] rounded-xl p-10 text-center cursor-pointer transition-colors"
                data-testid="import-file-dropzone"
              >
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="font-semibold text-gray-700">Klik om PDF of foto te selecteren</p>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG, HEIC of PDF — max 15 MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="import-file-input"
                />
              </div>

              {file && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-3">
                  {filePreview ? (
                    <img src={filePreview} alt="preview" className="w-16 h-16 object-cover rounded" />
                  ) : (
                    <FileText className="w-10 h-10 text-emerald-600" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-emerald-900 truncate">{file.name}</p>
                    <p className="text-xs text-emerald-700">{(file.size / 1024 / 1024).toFixed(2)} MB · {file.type}</p>
                  </div>
                  <Button size="sm" onClick={handleParse} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="import-parse-btn">
                    <Sparkles className="w-4 h-4 mr-1" /> AI analyseren
                  </Button>
                </div>
              )}
            </div>
          )}

          {phase === 'parsing' && (
            <div className="py-16 text-center">
              <Loader2 className="w-12 h-12 mx-auto text-[#3da5a5] animate-spin mb-4" />
              <p className="font-semibold text-gray-700">AI leest de {label.toLowerCase()}...</p>
              <p className="text-xs text-gray-500 mt-2">Dit kan 10-30 seconden duren afhankelijk van de grootte.</p>
            </div>
          )}

          {phase === 'review' && parsed && (
            <div className="space-y-4">
              {/* Klant match */}
              <div className={`rounded-lg p-3 border ${matchedClient ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                {matchedClient ? (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                    <span className="text-emerald-900">
                      <strong>Gekoppeld aan klant in CRM:</strong> {matchedClient.name} {matchedClient.email && `· ${matchedClient.email}`}
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0" />
                      <span className="text-amber-900"><strong>Geen bestaande klant gevonden</strong> voor "{parsed.client_name}"</span>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-amber-900 cursor-pointer">
                      <input type="checkbox" checked={createNewClient} onChange={(e) => setCreateNewClient(e.target.checked)} data-testid="import-create-client-cb" />
                      Nieuwe klant aanmaken in CRM bij opslaan
                    </label>
                  </div>
                )}
              </div>

              {/* Number duplicate warning */}
              {parsed.external_number && (
                <div className={`rounded-lg p-3 border text-sm ${duplicateNumber ? 'bg-red-50 border-red-200 text-red-900' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                  {duplicateNumber ? (
                    <>
                      <strong>⚠️ {label}nummer "{parsed.external_number}" bestaat al in het systeem.</strong> Er wordt automatisch een nieuw nummer toegekend.
                    </>
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={keepExternalNumber} onChange={(e) => setKeepExternalNumber(e.target.checked)} data-testid="import-keep-number-cb" />
                      <span>Originele {label.toLowerCase()}nummer "{parsed.external_number}" behouden (anders volgnummer)</span>
                    </label>
                  )}
                </div>
              )}

              {/* Header fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Klantnaam *</Label>
                  <Input value={parsed.client_name || ''} onChange={(e) => updateField('client_name', e.target.value)} data-testid="import-client-name" />
                </div>
                <div>
                  <Label className="text-xs">E-mail klant</Label>
                  <Input value={parsed.client_email || ''} onChange={(e) => updateField('client_email', e.target.value)} data-testid="import-client-email" />
                </div>
                <div>
                  <Label className="text-xs">Telefoon</Label>
                  <Input value={parsed.client_phone || ''} onChange={(e) => updateField('client_phone', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Adres</Label>
                  <Input value={parsed.address || ''} onChange={(e) => updateField('address', e.target.value)} data-testid="import-address" />
                </div>
                <div>
                  <Label className="text-xs">{label}nummer (origineel)</Label>
                  <Input value={parsed.external_number || ''} onChange={(e) => updateField('external_number', e.target.value)} data-testid="import-ext-number" />
                </div>
                <div>
                  <Label className="text-xs">Datum</Label>
                  <Input type="date" value={parsed.date || ''} onChange={(e) => updateField('date', e.target.value)} />
                </div>
                {documentType === 'quote' ? (
                  <div>
                    <Label className="text-xs">Vervaldatum offerte</Label>
                    <Input type="date" value={parsed.expiry_date || ''} onChange={(e) => updateField('expiry_date', e.target.value)} />
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">Vervaldatum factuur</Label>
                    <Input type="date" value={parsed.due_date || ''} onChange={(e) => updateField('due_date', e.target.value)} />
                  </div>
                )}
                <div>
                  <Label className="text-xs">BTW-regeling</Label>
                  <select className="w-full bg-gray-50 border border-gray-200 rounded-md p-2 text-sm" value={parsed.btw_status || 'normal'} onChange={(e) => updateField('btw_status', e.target.value)}>
                    <option value="normal">Normaal (BTW per regel)</option>
                    <option value="verlegd">BTW verlegd</option>
                  </select>
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-bold">Regels ({(parsed.items || []).length})</Label>
                  <Button size="sm" variant="outline" onClick={addItem}><Plus className="w-3 h-3 mr-1" /> Regel</Button>
                </div>
                <div className="space-y-2">
                  {(parsed.items || []).map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded border border-gray-200">
                      <Input className="col-span-5 h-8 text-xs" placeholder="Omschrijving" value={it.description || ''} onChange={(e) => updateItem(idx, 'description', e.target.value)} />
                      <Input className="col-span-1 h-8 text-xs" type="number" placeholder="Aantal" value={it.quantity || 0} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} />
                      <Input className="col-span-2 h-8 text-xs" type="number" placeholder="€" value={it.unit_price || 0} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} />
                      <Input className="col-span-1 h-8 text-xs" type="number" placeholder="Kort %" value={it.discount || 0} onChange={(e) => updateItem(idx, 'discount', e.target.value)} />
                      <select className="col-span-2 h-8 text-xs bg-white border border-gray-200 rounded px-1" value={it.btw ?? 21} onChange={(e) => updateItem(idx, 'btw', e.target.value)}>
                        <option value="0">0%</option>
                        <option value="9">9%</option>
                        <option value="21">21%</option>
                      </select>
                      <Button size="sm" variant="ghost" className="col-span-1 h-8 p-0" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Algemene korting + notities */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Algemene korting (€)</Label>
                  <Input type="number" value={parsed.general_discount || 0} onChange={(e) => updateField('general_discount', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Opmerkingen</Label>
                  <textarea className="w-full bg-gray-50 border border-gray-200 rounded-md p-2 text-sm h-20" value={parsed.notes || ''} onChange={(e) => updateField('notes', e.target.value)} />
                </div>
              </div>

              {/* Live totals */}
              <div className="bg-gradient-to-r from-[#3da5a5]/10 to-emerald-50 border border-[#3da5a5]/30 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><span className="text-gray-600">Subtotaal:</span> <strong>{fmtEuro(totals.subtotal)}</strong></div>
                  <div><span className="text-gray-600">BTW:</span> <strong>{fmtEuro(totals.btw_total)}</strong></div>
                  <div><span className="text-gray-600">Totaal incl.:</span> <strong className="text-[#3da5a5] text-lg">{fmtEuro(totals.total)}</strong></div>
                </div>
                {parsed.total && Math.abs(totals.total - parseFloat(parsed.total)) > 1 && (
                  <p className="text-xs text-amber-700 mt-2">⚠️ Berekend totaal ({fmtEuro(totals.total)}) wijkt af van wat AI extraheerde ({fmtEuro(parsed.total)}). Controleer de regels.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {phase === 'review' && (
          <div className="border-t border-gray-200 px-6 py-3 flex justify-between bg-gray-50">
            <Button variant="outline" onClick={() => setPhase('select')} disabled={saving}>← Opnieuw uploaden</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="import-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opslaan...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Opslaan in systeem</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
